import React from 'react';
import { Card, SectionHeader } from '@/components/Card';
import { Input } from '@/components/Input';
import { FormField } from '@/components/FormField';
import { TemplateButton } from '@/services/templates.service';
import { ShieldCheck, Copy, Clock, KeyRound, Lock, Lightbulb, Check } from 'lucide-react';
import clsx from 'clsx';

interface FormShape {
  buttons: TemplateButton[];
  footer?: string;
  previewSamples: Record<string, string>;
}

interface Props {
  form: FormShape;
  onChange: (patch: Partial<FormShape>) => void;
  /** Switch the template to category=Utility with an editable OTP-style body. */
  onSwitchToUtility?: () => void;
}

export const SEC_KEY = '__addSecurityRecommendation';

/** The body Meta generates for an authentication template (English), built
 *  from the editor's config — used by the live phone preview. */
export function buildAuthPreviewBody(previewSamples: Record<string, string>): string {
  const code = previewSamples['1'] || '184302';
  const security = previewSamples[SEC_KEY] !== '0' ? ' For your security, do not share this code.' : '';
  return `*${code}* is your verification code.${security}`;
}

/**
 * Specialised editor for `category === 'authentication'` templates.
 *
 * Meta's authentication-template body text is *not user-editable*. Meta
 * generates the body at delivery time using the recipient's locale; the
 * advertiser may only:
 *   - supply the OTP code (Meta substitutes for {{1}})
 *   - opt into appending the security disclaimer
 *     ("For your security, do not share this code.")
 *   - set a code-expiration footer (in minutes)
 *   - add a single "Copy code" button
 *
 * If the user wants custom wording (e.g. branding, a "thanks for signing up"
 * line), they should use category=Utility instead.
 */
export const AuthenticationEditor: React.FC<Props> = ({ form, onChange, onSwitchToUtility }) => {
  const copyButton = form.buttons.find((b) => b.type === 'COPY_CODE');
  const hasCopy = !!copyButton;
  const expiryMatch = form.footer?.match(/(\d+)/);
  const expiryMinutes = expiryMatch ? Number(expiryMatch[1]) : null;
  const sampleCode = form.previewSamples['1'] ?? '184302';
  const addSecurity = form.previewSamples[SEC_KEY] !== '0';   // default ON

  const toggleCopy = () => {
    if (hasCopy) {
      onChange({ buttons: form.buttons.filter((b) => b.type !== 'COPY_CODE') });
    } else {
      onChange({
        buttons: [
          ...form.buttons.filter((b) => b.type !== 'COPY_CODE'),
          { type: 'COPY_CODE', text: 'Copy code', url: '{{1}}' },
        ],
      });
    }
  };

  const updateCopyText = (text: string) => {
    onChange({
      buttons: form.buttons.map((b) => (b.type === 'COPY_CODE' ? { ...b, text } : b)),
    });
  };

  const setExpiry = (mins: number | null) => {
    if (!mins || mins <= 0) onChange({ footer: undefined });
    else onChange({ footer: `This code expires in ${mins} minutes.` });
  };

  const setSample = (val: string) => {
    onChange({ previewSamples: { ...form.previewSamples, '1': val } });
  };

  const setAddSecurity = (on: boolean) => {
    onChange({ previewSamples: { ...form.previewSamples, [SEC_KEY]: on ? '1' : '0' } });
  };

  return (
    <>
      {/* Limitation banner — set expectations clearly */}
      <div className="flex items-start gap-3 rounded-2xl border border-warning/30 bg-warning-soft px-4 py-3.5">
        <Lock size={16} className="mt-0.5 shrink-0 text-warning" />
        <div className="text-[13px] leading-relaxed text-text-secondary">
          <span className="font-semibold text-text-primary">Body text is not editable for Authentication templates.</span>{' '}
          Meta auto-generates the message ("<span className="font-mono">&lt;CODE&gt; is your verification code.</span>") in the recipient's language, so the same template works for every customer worldwide — and the code (<span className="font-mono">{'{{1}}'}</span>) is the <em>only</em> parameter Meta allows here. The fields below configure the OTP delivery.
          {onSwitchToUtility && (
            <div className="mt-2.5">
              <button
                type="button"
                onClick={onSwitchToUtility}
                className="rounded-lg border border-warning/40 bg-surface px-3 py-1.5 text-xs font-semibold text-text-primary transition-colors hover:border-warning hover:bg-warning-soft"
              >
                Write my own text instead → switch to Utility
              </button>
              <div className="mt-1.5 text-2xs text-text-muted">
                Utility templates allow custom wording and extra parameters (name, app, expiry…). Heads-up: Meta's review may reclassify an OTP-carrying utility template back to Authentication.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Body — fixed by Meta */}
      <Card padded={false} className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <SectionHeader
            title="OTP body (Meta-generated)"
            subtitle="Pick a sample code that Meta will use during template review."
            icon={<KeyRound size={15} />}
            className="mb-0"
          />
        </div>
        <div className="px-5 py-4 space-y-3">
          {/* Recipient preview */}
          <div className="rounded-lg border border-border bg-surface-alt/60 p-4">
            <div className="flex items-center justify-between">
              <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">Recipient sees</div>
              <div className="text-2xs italic text-text-muted">English example</div>
            </div>
            <div className="mt-2 space-y-1 text-text-primary">
              <div>
                <span className="font-mono text-base font-bold tracking-wide">{sampleCode}</span>
                <span className="ml-1.5 text-text-secondary">is your verification code.</span>
              </div>
              {addSecurity && (
                <div className="text-text-muted text-[13px]">For your security, do not share this code.</div>
              )}
              {expiryMinutes !== null && (
                <div className="text-text-muted text-[12px] italic mt-2">
                  This code expires in {expiryMinutes} {expiryMinutes === 1 ? 'minute' : 'minutes'}.
                </div>
              )}
            </div>
          </div>

          <FormField label="Sample code (for Meta's review)" hint="Numeric, 4–8 digits">
            <Input
              value={sampleCode}
              onChange={(e) => setSample(e.target.value.replace(/\D/g, '').slice(0, 8))}
              placeholder="184302"
              className="font-mono tracking-[0.4em]"
              maxLength={8}
            />
          </FormField>

          {/* Security recommendation toggle */}
          <div className="flex items-center justify-between rounded-lg border border-border bg-surface px-4 py-3">
            <div className="flex items-start gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-success-soft text-success">
                <ShieldCheck size={15} />
              </span>
              <div>
                <div className="text-sm font-semibold text-text-primary">Append security disclaimer</div>
                <div className="mt-0.5 text-xs text-text-muted">"For your security, do not share this code."</div>
              </div>
            </div>
            <Toggle on={addSecurity} onChange={setAddSecurity} />
          </div>
        </div>
      </Card>

      {/* One-time code: length + expiry */}
      <Card padded={false} className="overflow-hidden">
        <div className="border-b border-border px-5 py-4">
          <SectionHeader
            title="One-time code"
            subtitle="How long the code is, and how long it stays valid."
            icon={<Clock size={15} />}
            className="mb-0"
          />
        </div>
        <div className="space-y-5 px-5 py-4">
          <div>
            <div className="text-[13px] font-medium text-text-secondary">Code length</div>
            <div className="mb-2 text-2xs text-text-muted">The number of digits your app sends. The sample code follows it.</div>
            <div className="grid gap-3 sm:grid-cols-2">
              <OptionTile title="4 digits" hint="Shorter code" active={sampleCode.length === 4} onClick={() => setSample('1234')} />
              <OptionTile title="6 digits" hint="More secure" active={sampleCode.length !== 4} onClick={() => setSample('184302')} />
            </div>
          </div>
          <div>
            <div className="text-[13px] font-medium text-text-secondary">Code expiry time</div>
            <div className="mb-2 text-2xs text-text-muted">Shown under the message as "This code expires in N minutes."</div>
            <div className="grid gap-3 sm:grid-cols-3">
              <OptionTile title="5 minutes" hint="Standard security" active={expiryMinutes === 5} onClick={() => setExpiry(5)} />
              <OptionTile title="10 minutes" hint="Extended validity" active={expiryMinutes === 10} onClick={() => setExpiry(10)} />
              <OptionTile title="No expiry line" hint="Footer left out" active={expiryMinutes === null} onClick={() => setExpiry(null)} />
            </div>
            {expiryMinutes !== null && expiryMinutes !== 5 && expiryMinutes !== 10 && (
              <div className="mt-3 max-w-[200px]">
                <FormField label="Custom" hint="1–90 min">
                  <Input type="number" min={1} max={90} value={expiryMinutes} onChange={(e) => setExpiry(Number(e.target.value) || null)} />
                </FormField>
              </div>
            )}
            {(expiryMinutes === 5 || expiryMinutes === 10) && (
              <button type="button" onClick={() => setExpiry(15)} className="mt-2 text-xs font-medium text-primary hover:underline">Use a custom time…</button>
            )}
          </div>
        </div>
      </Card>

      {/* Copy code button */}
      <Card padded={false} className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <SectionHeader
            title='"Copy code" button'
            subtitle="One-tap button that copies the OTP to the recipient's clipboard."
            icon={<Copy size={15} />}
            className="mb-0"
          />
          <Toggle on={hasCopy} onChange={toggleCopy} />
        </div>
        {hasCopy && (
          <div className="px-5 py-4">
            <FormField label="Button label" hint={`${(copyButton?.text ?? '').length} / 25`}>
              <Input
                value={copyButton?.text ?? ''}
                maxLength={25}
                onChange={(e) => updateCopyText(e.target.value)}
                placeholder="Copy code"
              />
            </FormField>
          </div>
        )}
      </Card>

      {/* Tip */}
      <div className="flex items-start gap-2.5 rounded-xl border border-info/25 bg-info-soft px-4 py-3">
        <Lightbulb size={15} className="mt-0.5 shrink-0 text-info" />
        <div className="text-xs leading-relaxed text-text-secondary">
          <span className="font-semibold text-text-primary">Why so locked-down?</span> Authentication templates have to clear an extra Meta review process and ship in every language Meta supports. In exchange, they get higher delivery priority and bypass the 24-hour customer-care window. Worth it.
        </div>
      </div>
    </>
  );
};

const OptionTile: React.FC<{ title: string; hint: string; active: boolean; onClick: () => void }> = ({ title, hint, active, onClick }) => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onClick}
    className={clsx(
      'relative rounded-xl border-2 p-3.5 text-left transition-colors',
      active ? 'border-primary bg-primary-soft/60' : 'border-border hover:border-primary/40 hover:bg-surface-alt',
    )}
  >
    <span className="flex items-center gap-2.5">
      <span className={clsx('flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors', active ? 'border-primary' : 'border-border-strong')}>
        {active && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
      </span>
      <span className="min-w-0">
        <span className={clsx('block text-sm font-semibold', active ? 'text-primary' : 'text-text-primary')}>{title}</span>
        <span className="block text-xs text-text-muted">{hint}</span>
      </span>
    </span>
    {active && <Check size={16} className="absolute right-3 top-3 text-primary" />}
  </button>
);

const Toggle: React.FC<{ on: boolean; onChange: (on: boolean) => void }> = ({ on, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!on)}
    className={clsx(
      'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200',
      on ? 'bg-gold-gradient' : 'bg-border-strong',
    )}
    aria-pressed={on}
  >
    <span
      className={clsx(
        'inline-block h-5 w-5 transform rounded-full bg-white shadow-soft transition-transform duration-200',
        on ? 'translate-x-[22px]' : 'translate-x-0.5',
      )}
    />
  </button>
);
